import { useState } from 'react';
import { useNavigate } from 'react-router';
import { ThumbsUp, ThumbsDown, Mail, MessageCircle, Lightbulb } from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';
import { EXTERNAL_LINKS, EXTERNAL_LINK_PROPS } from '../../utils/externalLinks';
import { submitQuizFeedback } from '../../services/feedbackService';
import { toast } from 'sonner';

const QuizCompletionModal = ({ isOpen, sessionId, quizId, topicId = null, onViewResults }) => {
  const navigate = useNavigate();

  const [helpfulVote, setHelpfulVote] = useState(null); // 'up' | 'down' | null
  const [showReportOptions, setShowReportOptions] = useState(false);
  const [showSuggestForm, setShowSuggestForm] = useState(false);
  const [suggestion, setSuggestion] = useState('');
  const [isSubmittingSuggestion, setIsSubmittingSuggestion] = useState(false);
  const [isSubmittingVote, setIsSubmittingVote] = useState(false);

  if (!isOpen) return null;

  const handleHelpfulVote = async (vote) => {
    setIsSubmittingVote(true);
    try {
      await submitQuizFeedback(quizId, {
        is_helpful: vote === 'up',
      });
      setHelpfulVote(vote);
    } catch {
      toast.error('Failed to submit feedback.');
    } finally {
      setIsSubmittingVote(false);
    }
  };

  const handleSubmitSuggestion = async (e) => {
    e.preventDefault();
    if (!suggestion.trim()) return;
    setIsSubmittingSuggestion(true);
    try {
      await submitQuizFeedback(quizId, {
        is_helpful: helpfulVote === 'up' ? true : helpfulVote === 'down' ? false : undefined,
        feedback_text: suggestion,
      });
      toast.success('Thank you! Your suggestion has been submitted.');
      setSuggestion('');
      setShowSuggestForm(false);
    } catch {
      toast.error('Failed to submit suggestion. Please try again.');
    } finally {
      setIsSubmittingSuggestion(false);
    }
  };

  const mailtoLink = `mailto:${EXTERNAL_LINKS.supportEmail}?subject=Quiz Issue - Session ${sessionId}`;

  return (
    <div className="modal modal-open">
      <div className="modal-box text-center max-w-lg">
        <div className="text-5xl mb-4">🎉</div>
        <h3 className="font-bold text-xl mb-2">Quiz Completed!</h3>
        <p className="text-gray-600 mb-6">You've successfully completed the quiz.</p>

        {/* Was this quiz helpful? */}
        <div className="mb-6 pb-5 border-b border-base-300">
          <p className="text-sm font-medium text-gray-500 mb-3">Was this quiz helpful?</p>
          <div className="flex justify-center gap-4">
            <button
              className={`btn btn-sm gap-2 ${helpfulVote === 'up' ? 'btn-primary' : 'btn-outline'}`}
              onClick={() => handleHelpfulVote('up')}
              disabled={helpfulVote !== null || isSubmittingVote}
            >
              <ThumbsUp className="w-4 h-4" />
              Yes
            </button>
            <button
              className={`btn btn-sm gap-2 ${helpfulVote === 'down' ? 'btn-error' : 'btn-outline'}`}
              onClick={() => handleHelpfulVote('down')}
              disabled={helpfulVote !== null || isSubmittingVote}
            >
              <ThumbsDown className="w-4 h-4" />
              No
            </button>
          </div>
          {isSubmittingVote && (
            <p className="text-xs text-gray-400 mt-2">Submitting...</p>
          )}
          {helpfulVote && !isSubmittingVote && (
            <p className="text-xs text-gray-400 mt-2">Thanks for your feedback!</p>
          )}
        </div>

        {/* Report an Issue */}
        <div className="mb-4">
          <button
            className="btn btn-outline btn-sm w-full gap-2"
            onClick={() => {
              setShowReportOptions(!showReportOptions);
              setShowSuggestForm(false);
            }}
          >
            <MessageCircle className="w-4 h-4" />
            Report an Issue
          </button>
          {showReportOptions && (
            <div className="mt-3 space-y-2">
              <a
                href={mailtoLink}
                className="btn btn-outline btn-sm w-full gap-2"
              >
                <Mail className="w-4 h-4" />
                Send us an Email
              </a>
              {/* <a
                href={EXTERNAL_LINKS.whatsappGroup}
                {...EXTERNAL_LINK_PROPS}
                className="btn btn-outline btn-sm w-full gap-2"
              >
                <FaWhatsapp className="w-4 h-4 text-green-500" />
                Join WhatsApp Group
              </a> */}
            </div>
          )}
        </div>

        {/* Suggest Improvement */}
        <div className="mb-6">
          <button
            className="btn btn-outline btn-sm w-full gap-2"
            onClick={() => {
              setShowSuggestForm(!showSuggestForm);
              setShowReportOptions(false);
            }}
          >
            <Lightbulb className="w-4 h-4" />
            Suggest Improvement
          </button>
          {showSuggestForm && (
            <form onSubmit={handleSubmitSuggestion} className="mt-3 space-y-3">
              <textarea
                className="textarea textarea-bordered w-full"
                placeholder="Share your ideas for improving this quiz..."
                rows={3}
                value={suggestion}
                onChange={(e) => setSuggestion(e.target.value)}
                required
              />
              <button
                type="submit"
                className="btn btn-primary btn-sm w-full"
                disabled={isSubmittingSuggestion || !suggestion.trim()}
              >
                {isSubmittingSuggestion ? 'Submitting...' : 'Submit Suggestion'}
              </button>
            </form>
          )}
        </div>

        {/* View Results */}
        <div className="modal-action justify-center">
          <button
            className="btn btn-primary"
            onClick={() => {
              if (onViewResults) {
                onViewResults();
              } else {
                navigate(`/student/results/quizzes/${sessionId}/`, {
                  replace: true,
                  state: { topic_id: topicId, quiz_id: quizId },
                });
              }
            }}
          >
            View Results
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuizCompletionModal;